// Top search bar (Roadmap 1802): the server also finds clients through their
// SIN, phone, email and other details, in no useful order. Clients whose NAME
// matches what was typed come first, best match first; everyone else follows.
// Within each group the list is alphabetical.

const normalize = (value: string): string =>
  value.toLowerCase().replace(/\s+/g, " ").trim();

/** 0 = same name, 1 = name starts with it, 2 = a word starts with it,
 * 3 = name contains it, 4 = found through other details. */
export const nameMatchRank = (name: string, query: string): number => {
  const n = normalize(name);
  const q = normalize(query);
  if (!q || !n) return 4;
  if (n === q) return 0;
  if (n.startsWith(q)) return 1;
  if (n.split(/[\s,.&()/-]+/).some((word) => word && word.startsWith(q)))
    return 2;
  if (n.includes(q)) return 3;
  return 4;
};

const byName = (a: string, b: string): number =>
  normalize(a).localeCompare(normalize(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });

export const sortClientsForSearch = <T>(
  clients: readonly T[],
  query: string,
  getName: (client: T) => string | null | undefined,
): T[] =>
  clients
    .map((client) => {
      const name = getName(client) || "";
      return { client, name, rank: nameMatchRank(name, query) };
    })
    .sort((a, b) => a.rank - b.rank || byName(a.name, b.name))
    .map((row) => row.client);

// QA batch 8: a person can be an individual client AND show up again (as an
// organization's contact person, or twice through two emails), with the same
// name. Same client twice = one row; every client row says what it is
// ("Individual" / "Organization") and, when the name is shown more than once,
// the email too, so the rows can be told apart.
export type SearchClientRow = {
  customerId?: string | null;
  businessContactId?: string | null;
  customerName?: string | null;
  emailAddress?: string | null;
};

const clientKey = (row: SearchClientRow) =>
  row.businessContactId
    ? `org:${row.businessContactId.toLowerCase()}`
    : `ind:${(row.customerId || "").toLowerCase()}`;

export const uniqueClients = <T extends SearchClientRow>(
  rows: readonly T[],
): T[] => {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const key = clientKey(row);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const clientCaptions = <T extends SearchClientRow>(
  rows: readonly T[],
): string[] => {
  const nameCount = new Map<string, number>();
  rows.forEach((row) => {
    const name = normalize(row.customerName || "");
    nameCount.set(name, (nameCount.get(name) || 0) + 1);
  });
  return rows.map((row) => {
    const kind = row.businessContactId ? "Organization" : "Individual";
    const repeated =
      (nameCount.get(normalize(row.customerName || "")) || 0) > 1;
    const email = (row.emailAddress || "").trim();
    return repeated && email ? `${kind} · ${email}` : kind;
  });
};
